/**
 * @fileoverview `<FileInput>` — sélecteur de fichier stylé GNPC.
 *
 * Remplace le `<input type="file">` natif (bouton « Choisir un fichier »
 * incohérent avec le design system) par une zone cliquable en pointillés :
 * état vide (invite + formats acceptés) et état rempli (nom du fichier +
 * bouton pour retirer). Composant contrôlé sur le NOM du fichier — le fichier
 * réel est géré par le formulaire/back-office au branchement API.
 */

import { useRef } from 'react';
import { Paperclip, Upload, X } from 'lucide-react';
import { cn } from '../lib/utils.ts';

export interface FileInputProps {
  /** Id du champ, relié au `<label>` d'un `FormField`. */
  id?: string;
  /** Types acceptés, ex. `".pdf,.jpg,.png"`. */
  accept?: string;
  /** Aide sous l'invite : formats et taille max. */
  hint?: string;
  /** Nom du fichier sélectionné (`''` = aucun) — composant contrôlé. */
  fileName?: string;
  /** Appelé avec le nom du fichier choisi, ou `''` quand on le retire. */
  onFileChange: (fileName: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Sélecteur de fichier stylé (zone cliquable), sans ombre.
 *
 * @example
 * ```tsx
 * <FileInput
 *   fileName={name}
 *   onFileChange={setName}
 *   accept=".pdf,.jpg,.png"
 *   hint="PDF, JPG ou PNG · 5 Mo max"
 * />
 * ```
 */
export function FileInput({
  id,
  accept,
  hint,
  fileName,
  onFileChange,
  disabled,
  className,
}: FileInputProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  const clear = () => {
    onFileChange('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={cn('grid gap-2', className)}>
      <label
        htmlFor={id}
        className={cn(
          'border-input flex cursor-pointer items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm transition-colors',
          'hover:border-quipux-orange-500/60 hover:bg-quipux-orange-50/30',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-md',
            fileName
              ? 'bg-quipux-orange-100 text-quipux-orange-700'
              : 'bg-quipux-navy-100 text-quipux-navy-700',
          )}
        >
          {fileName ? <Paperclip className="size-4" /> : <Upload className="size-4" />}
        </span>
        <span className="min-w-0 flex-1">
          {fileName ? (
            <>
              <span className="text-quipux-navy-950 block truncate font-medium">{fileName}</span>
              <span className="text-muted-foreground text-xs">Cliquer pour remplacer</span>
            </>
          ) : (
            <>
              <span className="text-quipux-navy-950 block font-medium">
                Cliquer pour joindre un fichier
              </span>
              {hint ? <span className="text-muted-foreground text-xs">{hint}</span> : null}
            </>
          )}
        </span>
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          className="sr-only"
          onChange={(event) => onFileChange(event.target.files?.[0]?.name ?? '')}
        />
      </label>

      {fileName ? (
        <button
          type="button"
          onClick={clear}
          className="text-muted-foreground hover:text-danger inline-flex w-fit items-center gap-1 text-xs transition-colors"
        >
          <X className="size-3.5" aria-hidden="true" />
          Retirer le fichier
        </button>
      ) : null}
    </div>
  );
}
